import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SelectChangeEvent } from "@mui/material/Select";
import {
  AvailablePaymentCurrency,
  DonationFormCategory,
  TicketingFormCategory,
  formatCurrencyAmount,
  getDollarValue,
  getFeesFromCentValue,
  getCentsValue,
  Undefinable,
  OrganizationCountry,
  PaymentMethod,
  FeatureFlagValue,
  Nullable,
  FormType,
  StripeRecurringInterval,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { useLocaleContext } from "../../../../common/contexts/LocaleContext";
import { doesPaymentMethodAllowTip } from "../../../../common/helpers/payment-method";
import { trpc } from "../../../../common/helpers/trpc";

import { useTipSuggestionsQueryTrigger } from "./useTipSuggestionsQueryTrigger";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { NumberConstant } from "@/common/constants/number";
import {
  getForcedRepeatDonorVariant,
  REPEAT_DONOR_EXPERIMENT_QUERY_PARAM,
} from "@/common/constants/repeat-donor-experiment";
import { setUserProperty } from "@/common/helpers/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { TipSuggestionOutput } from "@/common/types/trpc";

const defaultTipSuggestions: TipSuggestionOutput[] = [
  {
    percentage: 5,
    amount: 0,
  },
  {
    percentage: 10,
    amount: 0,
  },
  {
    percentage: 15,
    amount: 0,
  },
];

export type SelectedTip = Partial<Pick<TipSuggestionOutput, "percentage" | "amount">> & {
  isFree?: boolean;
};

const hideTipSelectionKey = "hideTipSelection";

export const useComputeTip = ({
  displayedFormAmount,
  tipBaseAmount = displayedFormAmount,
  currency,
  organizationCountry,
  paymentMethod,
  isAuction,
  category,
  formData,
  shouldFetchTipSuggestions,
  hasDonorChangedAmount = false,
  repeatDonorToken = null,
  repeatDonorEmail = null,
  repeatDonorDeviceId = null,
  isRecurring,
  formType,
  stripeRecurrenceInterval,
  isMobile,
}: {
  displayedFormAmount: number;
  tipBaseAmount?: number;
  currency: AvailablePaymentCurrency;
  organizationCountry: Undefinable<OrganizationCountry>;
  paymentMethod: Undefinable<PaymentMethod>;
  isAuction: boolean;
  category: DonationFormCategory | Nullable<TicketingFormCategory> | undefined;
  formData: { id: string };
  shouldFetchTipSuggestions: boolean;
  hasDonorChangedAmount?: boolean;
  repeatDonorToken?: Nullable<string>;
  repeatDonorEmail?: Nullable<string>;
  repeatDonorDeviceId?: Nullable<string>;
  isRecurring?: boolean;
  formType?: FormType;
  stripeRecurrenceInterval?: StripeRecurringInterval | null;
  isMobile?: boolean;
}) => {
  const { logAmplitudeEvent } = useAmplitude();
  const searchParams = useSearchParams();
  const [selectedTip, setSelectedTip] = useState<SelectedTip>({
    amount: 0,
    percentage: 0,
    isFree: false,
  });

  const [suggestedTips, setSuggestedTips] = useState<TipSuggestionOutput[]>(defaultTipSuggestions);
  const [defaultTipSuggestion, setDefaultTipSuggestion] = useState<TipSuggestionOutput>(defaultTipSuggestions[0]);
  const [isSuggestedTipsAmount, setIsSuggestedTipsAmount] = useState(false);
  const [tipExperiment, setTipExperiment] = useState<{ name: string; value: FeatureFlagValue } | null>(null);
  const [freeTips, setFreeTips] = useState<number | "">("");
  const hasUserPickedTipRef = useRef(false);
  const hasUserDeclinedTipRef = useRef(false);
  const { isoLocale } = useLocaleContext();

  const tipSuggestionsQuery = useTipSuggestionsQueryTrigger({
    tipBaseAmount,
    displayedFormAmount,
    isPaymentStep: shouldFetchTipSuggestions,
    hasDonorChangedAmount,
  });

  const { data: tipsSuggestionData } = trpc.form_getFrontendTipSuggestions.useQuery(
    {
      amountInCents: tipSuggestionsQuery.tipBaseAmount,
      paymentAmountInCents: tipSuggestionsQuery.formAmount,
      organizationCountry: organizationCountry as OrganizationCountry,
      formCategory: category ?? undefined,
      repeatDonorToken: repeatDonorToken ?? undefined,
      repeatDonorEmail: repeatDonorEmail ?? undefined,
      repeatDonorAmplitudeDeviceId: repeatDonorDeviceId ?? undefined,
      qaForcedExperimentVariant:
        getForcedRepeatDonorVariant(searchParams?.get(REPEAT_DONOR_EXPERIMENT_QUERY_PARAM)) ?? undefined,
      isRecurring,
      formType,
      stripeRecurrenceInterval,
      isMobile,
    },
    {
      enabled: !(
        !tipSuggestionsQuery.isTriggered ||
        !tipSuggestionsQuery.formAmount ||
        tipSuggestionsQuery.formAmount >= NumberConstant.GRAPHQL_MAX_INT ||
        !organizationCountry
      ),
      staleTime: 5000,
    }
  );

  useEffect(() => {
    hasUserPickedTipRef.current = false;
    hasUserDeclinedTipRef.current = false;
  }, [displayedFormAmount]);

  useEffect(() => {
    if (displayedFormAmount === 0) {
      setSuggestedTips(defaultTipSuggestions);
      setDefaultTipSuggestion(defaultTipSuggestions[0]);
      setSelectedTip(defaultTipSuggestions[0]);
      setIsSuggestedTipsAmount(false);
      setTipExperiment(null);
      return;
    }

    if (!displayedFormAmount || displayedFormAmount >= NumberConstant.GRAPHQL_MAX_INT) {
      return;
    }

    if (!shouldFetchTipSuggestions) {
      return;
    }

    if (!tipsSuggestionData) {
      return;
    }

    const tipSuggestions = tipsSuggestionData.items as TipSuggestionOutput[] | undefined;

    if (tipSuggestions?.length) {
      const serverDefaultTipSuggestion =
        tipSuggestions.find((suggestion) => suggestion.percentage === tipsSuggestionData.defaultTipPercentage) ??
        tipSuggestions[0];
      setSuggestedTips(tipSuggestions);
      setDefaultTipSuggestion(serverDefaultTipSuggestion);
      setIsSuggestedTipsAmount(!isAuction && Boolean(tipsSuggestionData.isSuggestedTipsAmount));
      setTipExperiment(
        tipsSuggestionData.featureFlagName && tipsSuggestionData.featureFlagValue
          ? { name: tipsSuggestionData.featureFlagName, value: tipsSuggestionData.featureFlagValue }
          : null
      );
      if (tipsSuggestionData.paymentStepExperiment) {
        const { name, value } = tipsSuggestionData.paymentStepExperiment;
        setUserProperty(`exp_${name}`, value);
      }
      if (!hasUserPickedTipRef.current) {
        setSelectedTip(serverDefaultTipSuggestion);
      } else if (hasUserDeclinedTipRef.current && !tipSuggestions.some(({ amount }) => amount === 0)) {
        hasUserDeclinedTipRef.current = false;
        setSelectedTip({ isFree: true });
        setFreeTips(0);
      } else {
        setSelectedTip((currentTip) =>
          currentTip.isFree
            ? currentTip
            : (tipSuggestions.find((suggestion) => suggestion.percentage === currentTip.percentage) ??
              serverDefaultTipSuggestion)
        );
      }
    }
  }, [displayedFormAmount, isAuction, shouldFetchTipSuggestions, tipsSuggestionData]);

  // Only used on staging for a Seb big deal
  const hideTipSelection =
    process.env.NEXT_PUBLIC_ENV !== "production" && Boolean(searchParams.get(hideTipSelectionKey));

  const displayTipSelection = useMemo(
    () => Boolean(doesPaymentMethodAllowTip(paymentMethod) && displayedFormAmount > 0 && !hideTipSelection),
    [displayedFormAmount, hideTipSelection, paymentMethod]
  );

  const tip = useMemo(() => {
    return !displayTipSelection
      ? 0
      : selectedTip.isFree
        ? isNaN(freeTips as number)
          ? 0
          : getCentsValue(freeTips as number)
        : Math.round(selectedTip.amount as number);
  }, [freeTips, selectedTip, displayTipSelection]);

  const fees = useMemo(() => getDollarValue(getFeesFromCentValue(displayedFormAmount)), [displayedFormAmount]);

  const formattedFees = useMemo(
    () => formatCurrencyAmount({ value: fees, locale: isoLocale, currency }),
    [fees, isoLocale, currency]
  );

  const handleChangeTips = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      hasUserPickedTipRef.current = true;
      const selectedTip = suggestedTips.find((tip) => tip.percentage === event.target.value);
      hasUserDeclinedTipRef.current = selectedTip?.amount === 0;

      if (!selectedTip || event.target.value === "free") {
        setSelectedTip({ isFree: true });
        return;
      }
      setSelectedTip(selectedTip as TipSuggestionOutput);
    },
    [suggestedTips]
  );

  const getTipChoice = useCallback(
    (value: string) => {
      if (value === "free") {
        return "custom";
      }
      return suggestedTips.findIndex((suggestedTip) => suggestedTip.percentage === Number(value)) + 1;
    },
    [suggestedTips]
  );

  const handleChangeFreeTips = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    hasUserPickedTipRef.current = true;
    const freeTip = event.target.value === "" || event.target.value === null ? "" : Number(event.target.value);
    setFreeTips(freeTip);
  }, []);

  const logAmplitudeTipsChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      logAmplitudeEvent(AmplitudeEvents.DonorFormTipChanged, {
        amount: getDollarValue(displayedFormAmount),
        form_id: formData.id,
        form_type: category,
        organization_country: organizationCountry,
        tip_choice: "custom",
        tip_amount: Number(event.target.value),
      });
    },
    [category, displayedFormAmount, formData.id, logAmplitudeEvent, organizationCountry]
  );

  const resetTipToCoverBankCharge = useCallback(() => {
    hasUserPickedTipRef.current = true;
    setFreeTips(getDollarValue(Math.round(getFeesFromCentValue(displayedFormAmount))));
  }, [displayedFormAmount]);

  const updateFreeTipAmount = useCallback((amount: number) => {
    hasUserPickedTipRef.current = true;
    setFreeTips(amount);
  }, []);

  return {
    tip,
    suggestedTips,
    defaultTipSuggestion,
    selectedTip,
    isSuggestedTipsAmount,
    handleChangeTips,
    handleChangeFreeTips,
    resetTipToCoverBankCharge,
    freeTips,
    updateFreeTipAmount,
    fees,
    formattedFees,
    tipExperiment,
    getTipChoice,
    logAmplitudeTipsChanged,
    displayedFormAmount,
    displayTipSelection,
    prioritizeBankPayment: tipsSuggestionData?.prioritizeBankPayment,
    collapseRecommendedBankPayment: Boolean(tipsSuggestionData?.collapseRecommendedBankPayment),
    paymentStepExperiment: tipsSuggestionData?.paymentStepExperiment ?? null,
  };
};
